import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDepartments } from "../../../hooks/useDepartments";
import { projectService } from "../../../services/projectService";
import { departmentSubmissionService } from "../../../services/departmentSubmissionService";
import LoadingSpinner from "../../common/LoadingSpinner";
import { EmptyState } from "../../common/EmptyState";
import UniversalFilters from "../../common/UniversalFilters";
import { getFilterConfig } from "../../../constants/filterConfigs";
import { useFilters } from "../../../hooks/useFilters";
import ProjectStatusBadge from "./ProjectStatusBadge";
import ProjectDetailsModal from "./ProjectDetailsModal";
import ProjectEditModal from "./ProjectEditModal";
import Modal from "../../ui/Modal";
import Button from "../../ui/Button";
import {
  PROJECT_STATUS,
  formatFinancialRequirement,
  formatPriority,
} from "../../../constants/projectConstants";
import { USER_ROLES } from "../../../constants/authConstants";
import {
  dateUtils,
  arrayUtils,
  debounce,
  financialYearUtils,
} from "../../../utils/helpers";
import { validatePriority } from "../../../utils/projectValidation";
import { exportProjectHistoryPDF } from "../../../utils/pdfExport";

const ProjectHistory = () => {
  const { user, getUserRole, getUserDisplayName } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { getDepartmentLabel } = useDepartments();

  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  const isDepartment = getUserRole() === USER_ROLES.DEPARTMENT;

  // Project states
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingProject, setDeletingProject] = useState(null);

  // Priority editing states
  const [savingPriority, setSavingPriority] = useState({});
  const [priorityErrors, setPriorityErrors] = useState({});

  // Import/Export states
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [exporting, setExporting] = useState(false);

  const { filters, handleFilterChange, clearFilters, hasActiveFilters } =
    useFilters({
      search: "",
      financialYear: isDepartment ? financialYearUtils.getCurrentFinancialYear() : "",
      status: "",
      sort: "priority-asc",
    });

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProjectForDetails, setSelectedProjectForDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProjectForEdit, setSelectedProjectForEdit] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const filterConfig = getFilterConfig("PROJECT_HISTORY");

  const titleSize = getResponsiveValue({
    mobile: "text-xl",
    desktop: "text-2xl",
  });

  const cardPadding = getResponsiveValue({
    mobile: "p-4",
    desktop: "p-6",
  });

  // Check if table is locked
  const isTableLocked = currentSubmission?.isLocked || false;

  // Load projects and submissions on mount
  useEffect(() => {
    loadProjects();
    if (isDepartment) {
      loadCurrentSubmission();
    }
  }, [isDepartment]);

  // Load projects function
  const loadProjects = async () => {
    try {
      setLoading(true);
      const result = await projectService.getProjectsByUser(user.uid);

      if (result.success) {
        setProjects(result.data);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load current submission (for department users)
  const loadCurrentSubmission = async () => {
    try {
      setLoadingSubmission(true);
      const currentFY = financialYearUtils.getCurrentFinancialYear();
      const submission =
        await departmentSubmissionService.getSubmissionByDepartment(
          user.department,
          currentFY
        );
      setCurrentSubmission(submission);
    } catch (error) {
      console.error("Error loading submission:", error);
    } finally {
      setLoadingSubmission(false);
    }
  };

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...projects];

    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter((project) => {
        const deptLabel = getDepartmentLabel(project.department).toLowerCase();
        const projectName = project.projectName.toLowerCase();
        const brief = project.brief?.toLowerCase() || "";
        const fy = project.financialYear?.toLowerCase() || "";

        return (
          deptLabel.includes(searchLower) ||
          projectName.includes(searchLower) ||
          brief.includes(searchLower) ||
          fy.includes(searchLower)
        );
      });
    }

    if (filters.financialYear) {
      filtered = filtered.filter(
        (project) => project.financialYear === filters.financialYear
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (project) => project.status === filters.status
      );
    }

    // Two-level sorting
    filtered.sort((a, b) => {
      const deptA = getDepartmentLabel(a.department).toLowerCase();
      const deptB = getDepartmentLabel(b.department).toLowerCase();

      const deptComparison = deptA.localeCompare(deptB);

      if (deptComparison !== 0) {
        return deptComparison;
      }

      const aPriority = a.priority ? parseInt(a.priority) : 999999;
      const bPriority = b.priority ? parseInt(b.priority) : 999999;

      return aPriority - bPriority;
    });

    setFilteredProjects(filtered);
  }, [projects, filters.search, filters.financialYear, filters.status]);

  // Handle row click to show details
  const handleRowClick = (project) => {
    setSelectedProjectForDetails(project);
    setShowDetailsModal(true);
  };

  // Handle edit project
  const handleEditProject = (project) => {
    if (isTableLocked && !isAdmin) {
      alert("Your submission is locked. You cannot edit projects. Please contact admin to unlock.");
      return;
    }

    setSelectedProjectForEdit(project);
    setShowEditModal(true);
  };

  // Handle update project
  const handleUpdateProject = async (projectId, updatedData) => {
    try {
      const result = await projectService.updateProject(projectId, updatedData, user.uid);

      if (result.success) {
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId ? { ...p, ...updatedData, updatedAt: new Date() } : p
          )
        );

        setSuccessMessage(result.message);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error("Error updating project:", error);
      throw error;
    }
  };

  // Handle delete project
  const handleDeleteProject = async (projectId, projectName) => {
    if (isTableLocked && !isAdmin) {
      alert("Your submission is locked. You cannot delete projects. Please contact admin to unlock.");
      return;
    }

    if (!confirm(`Are you sure you want to delete "${projectName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingProject(projectId);
      const result = await projectService.deleteProject(projectId);

      if (result.success) {
        setProjects((prev) => prev.filter((p) => p.id !== projectId));
        setSuccessMessage(`Project "${projectName}" deleted successfully.`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project: " + error.message);
    } finally {
      setDeletingProject(null);
    }
  };

  // Debounced priority save
  const debouncedSavePriority = useCallback(
    debounce(async (projectId, priority) => {
      try {
        setSavingPriority((prev) => ({ ...prev, [projectId]: true }));
        await projectService.updateProjectPriority(projectId, priority, user.uid);
        setSavingPriority((prev) => ({ ...prev, [projectId]: false }));
      } catch (error) {
        console.error("Error saving priority:", error);
        setSavingPriority((prev) => ({ ...prev, [projectId]: false }));
        setPriorityErrors((prev) => ({ ...prev, [projectId]: "Failed to save priority" }));
      }
    }, 1000),
    [user.uid]
  );

  // Handle priority change
  const handlePriorityChange = (projectId, value) => {
    if (isTableLocked && !isAdmin) {
      alert("Your submission is locked. You cannot edit priorities. Please contact admin to unlock.");
      return;
    }

    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId ? { ...project, priority: value } : project
      )
    );

    setPriorityErrors((prev) => ({ ...prev, [projectId]: "" }));

    const validationError = validatePriority(value);
    if (validationError) {
      setPriorityErrors((prev) => ({ ...prev, [projectId]: validationError }));
      return;
    }

    if (value === "" || value === null) {
      debouncedSavePriority(projectId, null);
    } else {
      debouncedSavePriority(projectId, parseInt(value));
    }
  };

  // Handle import button click
  const handleImport = () => {
    if (currentSubmission && currentSubmission.isLocked) {
      alert("Your submission is locked. Please contact admin to make changes.");
      return;
    }
    setShowImportModal(true);
    setUploadFile(null);
    setUploadError("");
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setUploadError("");

    if (!file) return;

    if (file.type !== "application/pdf") {
      setUploadError("Only PDF files are allowed");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File size must be less than 10 MB");
      return;
    }

    setUploadFile(file);
  };

  // Handle upload submit
  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      setUploadError("Please select a PDF file");
      return;
    }

    try {
      setUploading(true);
      setUploadError("");

      const currentFY = financialYearUtils.getCurrentFinancialYear();
      const result = await departmentSubmissionService.uploadSubmission(
        uploadFile,
        user.department,
        currentFY,
        user.uid,
        getUserDisplayName()
      );

      if (result.success) {
        setSuccessMessage(result.message);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);

        setShowImportModal(false);
        loadCurrentSubmission();
      }
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle lock submission
  const handleLockSubmission = async () => {
    if (!currentSubmission) {
      alert("No submission found to lock");
      return;
    }

    if (!confirm("Are you sure you want to lock your submission?\n\nOnce locked, you will not be able to:\n- Upload a new file\n- Edit project details\n- Delete projects\n- Change priorities\n\nAn admin will need to unlock it for you to make changes.")) {
      return;
    }

    try {
      const result = await departmentSubmissionService.lockSubmission(
        currentSubmission.id,
        user.uid,
        getUserDisplayName()
      );

      if (result.success) {
        setSuccessMessage(result.message);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);
        loadCurrentSubmission();
      }
    } catch (error) {
      alert("Failed to lock submission: " + error.message);
    }
  };

  // Handle export
  const handleExport = () => {
    try {
      setExporting(true);
      const departmentName = getDepartmentLabel(user.department);
      exportProjectHistoryPDF(filteredProjects, departmentName, filters);
      setSuccessMessage("PDF exported successfully!");
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Failed to export PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading project history..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Locked Warning Banner */}
      {isDepartment && isTableLocked && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-800">Submission Locked - Read Only Mode</p>
              <p className="text-xs text-orange-600 mt-1">Your project data is locked. You cannot edit, delete, or modify priorities.</p>
            </div>
          </div>
        </div>
      )}

      {/* Submission Status Banner (Department User Only) */}
      {isDepartment && !loadingSubmission && (
        <div className={`rounded-xl p-4 ${currentSubmission ? currentSubmission.isLocked ? "bg-green-50 border border-green-200" : "bg-blue-50 border border-blue-200" : "bg-yellow-50 border border-yellow-200"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {currentSubmission ? (
                currentSubmission.isLocked ? (
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                )
              ) : (
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <div>
                {currentSubmission ? (
                  currentSubmission.isLocked ? (
                    <>
                      <p className="text-sm font-medium text-green-800">Document Locked</p>
                      <p className="text-xs text-green-600">Submitted: {currentSubmission.fileName} on {dateUtils.formatDate(currentSubmission.lockedAt)}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-blue-800">Document Uploaded (Draft)</p>
                      <p className="text-xs text-blue-600">{currentSubmission.fileName} • {departmentSubmissionService.formatFileSize(currentSubmission.fileSize)}</p>
                    </>
                  )
                ) : (
                  <>
                    <p className="text-sm font-medium text-yellow-800">No Document Uploaded</p>
                    <p className="text-xs text-yellow-600">Upload your project proposal document to proceed</p>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {currentSubmission && !currentSubmission.isLocked && (
                <Button onClick={handleLockSubmission} size="sm" variant="primary" className="bg-green-600 hover:bg-green-700">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Lock & Submit
                </Button>
              )}
              {/* UPDATED: View button only for Admin */}
              {currentSubmission && isAdmin && (
                <Button onClick={() => window.open(currentSubmission.fileUrl, "_blank")} size="sm" variant="outline">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>Project Proposal Status</h1>
          <p className="text-text-secondary text-sm">View your submitted project history and status updates</p>
        </div>

        <div className="flex items-center space-x-3">
          {isDepartment && (
            <>
              <Button onClick={handleImport} size="md" variant="outline" className="flex items-center space-x-2" disabled={currentSubmission && currentSubmission.isLocked}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>Upload</span>
              </Button>

              <Button onClick={handleExport} size="md" variant="outline" className="flex items-center space-x-2" disabled={exporting || filteredProjects.length === 0} loading={exporting}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{exporting ? "Downloading..." : "Download"}</span>
              </Button>
            </>
          )}

          <UniversalFilters config={filterConfig} values={filters} onChange={handleFilterChange} onClearFilters={clearFilters} mode="dropdown" />
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredProjects.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={projects.length === 0 ? "No projects submitted yet" : "No projects match your filters"}
              description={projects.length === 0 ? "Start by creating your first project in the Planning Form" : "Try adjusting your search or filter criteria"}
              actionLabel={hasActiveFilters ? "Clear Filters" : null}
              onAction={hasActiveFilters ? clearFilters : null}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">S. No.</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">Department</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">Project Name</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">Financial Year</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                    Proposed Total Cost<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  {isAdmin && (
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                      Approved Total Cost<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                    </th>
                  )}
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">Priority</th>
                  {isAdmin && (
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">Status</th>
                  )}
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">Submitted</th>
                  {isAdmin && (
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20 border-r border-gray-200">Last Updated</th>
                  )}
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredProjects.map((project, index) => (
                  <tr key={project.id} className="hover:bg-blue-50 transition-colors duration-200">
                    <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">{index + 1}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200">
                      {getDepartmentLabel(project.department)}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-medium border-r border-gray-200 cursor-pointer" onClick={() => handleRowClick(project)}>
                      <div className="max-w-xs">
                        <div className="font-medium line-clamp-2 hover:text-blue-600 transition-colors">{project.projectName}</div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                      {project.financialYear ? `FY ${project.financialYear}` : "N/A"}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-gray-200">
                      {formatFinancialRequirement(project.financialRequirement)}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-3 text-center text-xs font-medium border-r border-gray-200">
                        {project.status === PROJECT_STATUS.APPROVED && project.approvedAmount ? (
                          <span className="text-green-600">{formatFinancialRequirement(project.approvedAmount)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-3 text-center border-r border-gray-200" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="number"
                          min="1"
                          max="999"
                          value={project.priority || ""}
                          onChange={(e) => handlePriorityChange(project.id, e.target.value)}
                          placeholder="—"
                          disabled={isTableLocked && !isAdmin}
                          className={`w-16 text-xs text-center border rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            priorityErrors[project.id] ? "border-red-500" : "border-gray-300"
                          } ${isTableLocked && !isAdmin ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}`}
                        />
                        {savingPriority[project.id] && (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                          </div>
                        )}
                        {priorityErrors[project.id] && (
                          <div className="text-xs text-red-600 font-medium whitespace-nowrap">{priorityErrors[project.id]}</div>
                        )}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-3 text-center border-r border-gray-200">
                        <ProjectStatusBadge status={project.status} />
                      </td>
                    )}
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                      {dateUtils.formatDate(project.createdAt)}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                        {project.status !== PROJECT_STATUS.PENDING ? dateUtils.formatDate(project.approvedAt || project.updatedAt) : "—"}
                      </td>
                    )}
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-2">
                        {(!isTableLocked || isAdmin) && project.status === PROJECT_STATUS.PENDING && (
                          <button onClick={() => handleEditProject(project)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit project">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}

                        {(!isTableLocked || isAdmin) && (
                          <button onClick={() => handleDeleteProject(project.id, project.projectName)} disabled={deletingProject === project.id} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Delete project">
                            {deletingProject === project.id ? (
                              <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-600"></div>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        )}

                        {isTableLocked && !isAdmin && (
                          <div className="p-1.5 text-gray-400" title="Locked - Contact admin to unlock">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Project Details Modal */}
      <ProjectDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedProjectForDetails(null);
        }}
        project={selectedProjectForDetails}
      />

      {/* Edit Modal */}
      <ProjectEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProjectForEdit(null);
        }}
        project={selectedProjectForEdit}
        onUpdate={handleUpdateProject}
        isLocked={isTableLocked}
      />

      {/* Import Modal (Department Users Only) */}
      {isDepartment && (
        <Modal
          isOpen={showImportModal}
          onClose={() => !uploading && setShowImportModal(false)}
          title="Upload Project Proposal Document"
          size="md"
        >
          <div className="p-6 space-y-6">
            <p className="text-sm text-text-secondary">
              Upload your project proposal PDF document. You can replace this file until you lock your submission.
            </p>

            {uploadError && (
              <div className="bg-error-subtle border border-border-error rounded-xl p-3">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-error text-sm">{uploadError}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                PDF Document <span className="text-error">*</span>
              </label>
              <div className="flex items-center justify-center w-full">
                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-border-primary border-dashed rounded-xl cursor-pointer bg-surface-secondary hover:bg-surface-hover transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    {uploadFile ? (
                      <>
                        <svg className="w-10 h-10 mb-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <p className="mb-2 text-sm text-text-primary font-medium">{uploadFile.name}</p>
                        <p className="text-xs text-text-tertiary">{departmentSubmissionService.formatFileSize(uploadFile.size)}</p>
                      </>
                    ) : (
                      <>
                        <svg className="w-10 h-10 mb-3 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="mb-2 text-sm text-text-secondary">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-text-tertiary">PDF only (Max 10 MB)</p>
                      </>
                    )}
                  </div>
                  <input type="file" className="hidden" accept="application/pdf" onChange={handleFileSelect} disabled={uploading} />
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-border-primary">
              <Button variant="outline" onClick={() => setShowImportModal(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUploadSubmit} loading={uploading} disabled={uploading || !uploadFile}>
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProjectHistory;